/**
 * One-time app-orientation tour shown to first-time visitors on the dashboard.
 *
 * WHEN IT SHOWS
 * 600 ms after the dashboard (`/`) first renders, giving content and
 * server-hydrated progress time to settle.  Suppressed when either guard is
 * true:
 *   1. Local store (`app-tour:v1`) - fast, persists per employee on this device.
 *   2. Server-hydrated stage - `stages[APP_TOUR_SUB_MODULE_ID]?.guided` is
 *      populated by `hydrateStages` at boot and survives a localStorage clear.
 *
 * COMPLETION
 * Completing all steps marks the local store, patches the in-memory
 * lesson-progress store, and posts to `POST /api/progress/completions` so the
 * Sheet records it. Dismissing (X) marks the local store only.
 *
 * LANGUAGE
 * Step content is read from the `player` i18n namespace (`appTour.*` keys) so
 * it follows the `?lang=` URL param that controls the rest of the app.
 */

import { BarChart3, GraduationCap, Play, Settings } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useLessonProgress } from '@/player/lesson-progress'
import { getSessionIdentity } from '@/session/identity'
import {
  getElementRect,
  postTourCompletion,
  type Rect,
  SCROLL_SETTLE_MS,
  SHOW_DELAY_MS,
  Spotlight,
  TourCard,
  type TourStepMeta,
} from './tour-overlay-shared'
import { APP_TOUR_MODULE_ID, APP_TOUR_SUB_MODULE_ID, useAppTour } from './use-app-tour'

// ─── Step metadata (IDs + icons stay constant; titles/bodies come from i18n) ──

const STEP_META: readonly TourStepMeta[] = [
  { id: 'welcome', Icon: GraduationCap },
  { id: 'startLesson', target: 'modules-section', Icon: Play },
  { id: 'progress', target: 'progress-card', Icon: BarChart3 },
  { id: 'accessibility', target: 'accessibility-btn', Icon: Settings },
]

const STEP_IDS = STEP_META.map((s) => s.id)

// ─── Component ────────────────────────────────────────────────────────────────

export function AppTour() {
  const { t } = useTranslation('player')
  const location = useLocation()
  const isOnDashboard = location.pathname === '/'

  const seenBy = useAppTour((s) => s.seenBy)
  const markSeen = useAppTour((s) => s.markSeen)
  const stages = useLessonProgress((s) => s.stages)
  const identity = getSessionIdentity()

  const alreadySeen = useMemo(
    () =>
      (identity !== null && seenBy[identity.employeeId] === true) ||
      stages[APP_TOUR_SUB_MODULE_ID]?.guided !== undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity, seenBy, stages],
  )

  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [spotlightRect, setSpotlightRect] = useState<Rect | null>(null)

  const meta = STEP_META[stepIndex]

  // Reveal after a delay - only on the dashboard and only once.
  useEffect(() => {
    if (alreadySeen || dismissed || !isOnDashboard) return
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(t)
  }, [alreadySeen, dismissed, isOnDashboard])

  // Hide when navigating away mid-tour.
  useEffect(() => {
    if (!isOnDashboard) setVisible(false)
  }, [isOnDashboard])

  // Scroll target into view and measure rect on each step change.
  useEffect(() => {
    if (!visible || !meta?.target) {
      setSpotlightRect(null)
      return
    }
    const el = document.querySelector(`[data-app-tour="${meta.target}"]`)
    if (!el) {
      setSpotlightRect(null)
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    const timer = setTimeout(
      () => setSpotlightRect(getElementRect(meta.target ?? '')),
      SCROLL_SETTLE_MS,
    )
    return () => clearTimeout(timer)
  }, [visible, meta?.target])

  const dismiss = (completed: boolean) => {
    setVisible(false)
    setDismissed(true)
    if (identity) markSeen(identity.employeeId)
    if (completed) {
      useLessonProgress.getState().hydrateStages([
        {
          subModuleId: APP_TOUR_SUB_MODULE_ID,
          moduleId: APP_TOUR_MODULE_ID,
          completedAt: new Date().toISOString(),
          stage: 'guided',
          requiresUnguided: false,
        },
      ])
      postTourCompletion(APP_TOUR_MODULE_ID, APP_TOUR_SUB_MODULE_ID)
    }
  }

  const handleNext = () => {
    if (stepIndex < STEP_META.length - 1) setStepIndex((i) => i + 1)
    else dismiss(true)
  }
  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1))
  const handleSkip = () => dismiss(false)

  if (!visible || alreadySeen || dismissed || !meta) return null

  const hasSpotlight = !!meta.target

  return (
    <div className="fixed inset-0" style={{ zIndex: 9900 }}>
      {!hasSpotlight && (
        <div aria-hidden="true" className="absolute inset-0 bg-black/75" onClick={handleSkip} />
      )}
      {hasSpotlight && spotlightRect && <Spotlight rect={spotlightRect} />}
      {hasSpotlight && !spotlightRect && (
        <div aria-hidden="true" className="absolute inset-0 bg-black/75" />
      )}

      <TourCard
        Icon={meta.Icon}
        title={t(`appTour.${meta.id}.title`)}
        body={t(`appTour.${meta.id}.body`)}
        stepIndex={stepIndex}
        totalSteps={STEP_META.length}
        stepIds={STEP_IDS}
        spotlightRect={spotlightRect}
        lastStepLabel={t('tour.startLearning')}
        onBack={handleBack}
        onNext={handleNext}
        onSkip={handleSkip}
      />
    </div>
  )
}
